const CAMPAIGN_VALUE_MAX_LENGTH = 120;

const CAMPAIGN_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
] as const;

export type CampaignAttributionKey = (typeof CAMPAIGN_KEYS)[number];

export type CampaignAttribution = Partial<
  Record<CampaignAttributionKey, string>
>;

type SearchParamValue = string | string[] | null | undefined;

export type CampaignSearchParams = Partial<
  Record<CampaignAttributionKey, SearchParamValue>
>;

function firstValue(value: SearchParamValue): string | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

function sanitiseCampaignValue(value: SearchParamValue): string | null {
  const candidate = firstValue(value);
  if (typeof candidate !== "string") return null;

  const sanitised = candidate
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .slice(0, CAMPAIGN_VALUE_MAX_LENGTH);

  return sanitised || null;
}

/**
 * Return a bounded set of standard campaign properties. Unknown fields are
 * ignored so arbitrary query-string data never reaches signup analytics.
 */
export function parseCampaignAttribution(
  params: CampaignSearchParams,
): CampaignAttribution | null {
  const attribution: CampaignAttribution = {};

  for (const key of CAMPAIGN_KEYS) {
    const value = sanitiseCampaignValue(params[key]);
    if (value) attribution[key] = value;
  }

  return Object.keys(attribution).length > 0 ? attribution : null;
}

/**
 * Re-validate attribution restored from local or database-backed signup
 * drafts before attaching it to an analytics event.
 */
export function parseStoredCampaignAttribution(
  value: unknown,
): CampaignAttribution | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const stored = value as Record<string, unknown>;
  return parseCampaignAttribution({
    utm_source:
      typeof stored.utm_source === "string" ? stored.utm_source : undefined,
    utm_medium:
      typeof stored.utm_medium === "string" ? stored.utm_medium : undefined,
    utm_campaign:
      typeof stored.utm_campaign === "string" ? stored.utm_campaign : undefined,
    utm_content:
      typeof stored.utm_content === "string" ? stored.utm_content : undefined,
  });
}
